import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { Company, CompanyCreate, CompanyUpdate } from '../../types/models';
import { formatDate } from '../../utils/formatters';

const CompaniesList: React.FC = () => {
  const { user } = useAuth();
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Company form dialog
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Form fields
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPayer, setIsPayer] = useState(false);
  const [isIssuer, setIsIssuer] = useState(false);
  const [score, setScore] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  
  // Delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<Company[]>('/companies');
      setCompanies(response);
    } catch (err: any) {
      console.error('Error fetching companies:', err);
      setError(err.response?.data?.detail || 'Error al cargar las empresas');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCompanies();
  }, []);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleOpenCreateDialog = () => {
    setFormMode('create');
    setSelectedCompany(null);
    resetForm();
    setOpenFormDialog(true);
  };
  
  const handleOpenEditDialog = (company: Company) => {
    setFormMode('edit');
    setSelectedCompany(company);
    
    // Populate form fields
    setName(company.name);
    setTaxId(company.tax_id);
    setAddress(company.address || '');
    setPhone(company.phone || '');
    setEmail(company.email || '');
    setIsPayer(company.is_payer || false);
    setIsIssuer(company.is_issuer || false);
    setScore(company.score || '');
    setContactName(company.contact_name || '');
    setContactEmail(company.contact_email || '');
    setContactPhone(company.contact_phone || '');
    
    setOpenFormDialog(true);
  };
  
  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setFormError(null);
  };
  
  const resetForm = () => {
    setName('');
    setTaxId('');
    setAddress('');
    setPhone('');
    setEmail('');
    setIsPayer(false);
    setIsIssuer(false);
    setScore('');
    setContactName('');
    setContactEmail('');
    setContactPhone('');
  };
  
  const validateForm = () => {
    if (!name.trim()) {
      setFormError('El nombre de la empresa es requerido');
      return false;
    }
    if (!taxId.trim()) {
      setFormError('El RUC es requerido');
      return false;
    }
    return true;
  };
  
  const handleSubmitForm = async () => {
    if (!validateForm()) return;
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      if (formMode === 'create') {
        const companyData: CompanyCreate = {
          name,
          tax_id: taxId,
          address: address || undefined,
          phone: phone || undefined,
          email: email || undefined,
          is_payer: isPayer,
          is_issuer: isIssuer,
          score: score || undefined,
          contact_name: contactName || undefined,
          contact_email: contactEmail || undefined,
          contact_phone: contactPhone || undefined
        };
        
        await apiService.post('/companies', companyData);
        setActionSuccess('Empresa creada exitosamente');
      } else if (formMode === 'edit' && selectedCompany) {
        const companyData: CompanyUpdate = {
          name,
          tax_id: taxId,
          address: address || undefined,
          phone: phone || undefined,
          email: email || undefined,
          is_payer: isPayer,
          is_issuer: isIssuer,
          score: score || undefined,
          contact_name: contactName || undefined,
          contact_email: contactEmail || undefined,
          contact_phone: contactPhone || undefined
        };
        
        await apiService.put(`/companies/${selectedCompany.id}`, companyData);
        setActionSuccess('Empresa actualizada exitosamente');
      }
      
      // Refresh companies list
      fetchCompanies();
      
      // Close dialog
      handleCloseFormDialog();
    } catch (err: any) {
      console.error('Error saving company:', err);
      setFormError(err.response?.data?.detail || 'Error al guardar la empresa');
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleOpenDeleteDialog = (company: Company) => {
    setSelectedCompany(company);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };
  
  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    
    setDeleteLoading(true);
    
    try {
      await apiService.delete(`/companies/${selectedCompany.id}`);
      
      // Update local state
      setCompanies(companies.filter(company => company.id !== selectedCompany.id));
      
      // Show success message
      setActionSuccess(`Empresa "${selectedCompany.name}" eliminada exitosamente`);
      
      // Close dialog
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting company:', err);
      setError(err.response?.data?.detail || 'Error al eliminar la empresa');
      handleCloseDeleteDialog();
    } finally {
      setDeleteLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Empresas</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Empresas
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchCompanies}
            sx={{ mr: 1 }}
          >
            Actualizar
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Agregar Empresa
          </Button>
        </Box>
      </Box>
      
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : companies.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No se encontraron empresas</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agrega una nueva empresa para comenzar
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Agregar Empresa
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="companies table">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>RUC</TableCell>
                  <TableCell>Dirección</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Creado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell>{company.name}</TableCell>
                      <TableCell>{company.tax_id}</TableCell>
                      <TableCell>{company.address || 'N/A'}</TableCell>
                      <TableCell>{company.phone || 'N/A'}</TableCell>
                      <TableCell>{company.email || 'N/A'}</TableCell>
                      <TableCell>
                        {company.is_payer && <Chip label="Pagador" size="small" color="primary" sx={{ mr: 0.5 }} />}
                        {company.is_issuer && <Chip label="Emisor" size="small" color="secondary" />}
                      </TableCell>
                      <TableCell>{company.score || 'N/A'}</TableCell>
                      <TableCell>{formatDate(company.created_at)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Editar Empresa">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(company)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar Empresa">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(company)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={companies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* Company Form Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {formMode === 'create' ? 'Agregar Nueva Empresa' : 'Editar Empresa'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Información Básica</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nombre de la Empresa"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="RUC"
                fullWidth
                required
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Dirección"
                fullWidth
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Tipo y Calificación</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPayer}
                      onChange={(e) => setIsPayer(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Es Pagador"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isIssuer}
                      onChange={(e) => setIsIssuer(e.target.checked)}
                      disabled={formLoading}
                    />
                  }
                  label="Es Emisor"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Puntaje"
                fullWidth
                value={score}
                onChange={(e) => setScore(e.target.value)}
                disabled={formLoading}
                helperText="Calificación de la empresa"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Información de Contacto</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Nombre de Contacto"
                fullWidth
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Email de Contacto"
                type="email"
                fullWidth
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Teléfono de Contacto"
                fullWidth
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                disabled={formLoading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog} disabled={formLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmitForm}
            variant="contained"
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
          >
            {formLoading 
              ? 'Guardando...' 
              : formMode === 'create' 
                ? 'Crear' 
                : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        {selectedCompany && (
          <>
            <DialogTitle>
              Eliminar Empresa
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                ¿Estás seguro de que deseas eliminar la empresa "{selectedCompany.name}"? Esta acción no se puede deshacer.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDeleteCompany}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
              >
                {deleteLoading ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default CompaniesList;
